import { Badge } from 'primereact/badge'
import { Dialog } from 'primereact/dialog'
import { ProgressSpinner } from 'primereact/progressspinner'
import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCamera } from '../../cameras/hooks/useCamera'
import './stream-view.scss'
import CameraPlayer from './camera-stream-player'
import { useAuth } from 'layout/context/auth-context'
import { useIsMobile } from 'shared/hooks/use-is-mobile'
import { Camera } from 'domain/camera'

interface FullscreenPlayerProps {
  camera: Camera
  visible: boolean
  onHide: () => void
}

const FullscreenPlayer: React.FC<FullscreenPlayerProps> = ({
  camera,
  visible,
  onHide,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const attemptsRef = useRef(0)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const maxAttempts = 5
  const [status, setStatus] = useState<'connecting' | 'connected' | 'error'>(
    'connecting',
  )
  const { tokens } = useAuth()

  useEffect(() => {
    if (!visible) return

    attemptsRef.current = 0

    const getRetryDelay = (attempt: number) =>
      Math.min(1000 * 2 ** (attempt - 1), 16000)

    const startStream = async () => {
      if (attemptsRef.current >= maxAttempts) {
        setStatus('error')
        return
      }

      attemptsRef.current += 1
      setStatus('connecting')

      try {
        if (pcRef.current) {
          pcRef.current.close()
        }

        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        })
        pcRef.current = pc

        pc.addTransceiver('video', { direction: 'recvonly' })
        pc.addTransceiver('audio', { direction: 'recvonly' })

        pc.ontrack = (event) => {
          if (videoRef.current && event.streams[0]) {
            videoRef.current.srcObject = event.streams[0]
            attemptsRef.current = 0
            setStatus('connected')
          }
        }

        pc.onconnectionstatechange = () => {
          if (
            pc.connectionState === 'failed' ||
            pc.connectionState === 'disconnected'
          ) {
            const delay = getRetryDelay(attemptsRef.current)
            retryTimeoutRef.current = setTimeout(startStream, delay)
          }
        }

        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        await new Promise<void>((resolve) => {
          if (pc.iceGatheringState === 'complete') resolve()
          else
            pc.onicegatheringstatechange = () => {
              if (pc.iceGatheringState === 'complete') resolve()
            }
        })

        const mediamtxUrl: string = process.env.REACT_APP_API_BASE_URL ?? ''
        const response = await fetch(
          `${mediamtxUrl}/stream/${camera.id}/whep`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/sdp',
              Authorization: `Bearer ${tokens?.accessToken}`,
            },
            body: pc.localDescription!.sdp,
          },
        )

        if (!response.ok) throw new Error(`WHEP failed: ${response.status}`)

        const answerSdp = await response.text()
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
      } catch (err) {
        console.error(`[Fullscreen] Stream error (attempt ${attemptsRef.current}):`, err)
        if (attemptsRef.current < maxAttempts) {
          const delay = getRetryDelay(attemptsRef.current)
          retryTimeoutRef.current = setTimeout(startStream, delay)
        } else {
          setStatus('error')
        }
      }
    }

    startStream()

    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
      if (pcRef.current) {
        pcRef.current.close()
        pcRef.current = null
      }
    }
  }, [camera.streamPath, visible])

  const getStatusBadge = () => {
    switch (status) {
      case 'connected':
        return (
          <Badge
            value='Transmissão ao vivo'
            severity='success'
          />
        )
      case 'connecting':
        return (
          <Badge
            value='Conectando...'
            severity='warning'
            className='pulse'
          />
        )
      case 'error':
        return (
          <Badge
            value='Desconectado'
            severity='danger'
          />
        )
    }
  }

  const header = (
    <div className='fullscreen-header'>
      <div className='fullscreen-title'>
        <i className='pi pi-video' />
        <div>
          <h3>{camera.name}</h3>
          {getStatusBadge()}
        </div>
      </div>
    </div>
  )

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      maximizable
      maximized
      className='fullscreen-dialog'
    >
      <div className='fullscreen-video-container'>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className='fullscreen-video'
        />

        {status === 'connecting' && (
          <div className='fullscreen-overlay'>
            <ProgressSpinner
              style={{ width: '80px', height: '80px' }}
              strokeWidth='4'
              animationDuration='1s'
            />
            <p>Conectando à câmera</p>
            <small>Aguarde enquanto estabelecemos a conexão...</small>
          </div>
        )}

        {status === 'error' && (
          <div className='fullscreen-overlay error'>
            <i
              className='pi pi-exclamation-circle'
              style={{ fontSize: '4rem' }}
            />
            <p>Falha na Conexão</p>
            <small>Não foi possível conectar à câmera</small>
          </div>
        )}
      </div>
    </Dialog>
  )
}

const StreamView = () => {
  const { clientId } = useAuth()
  const { cameras, fetchCameras } = useCamera()

  React.useEffect(() => {
    if (clientId) fetchCameras({ clientId })
  }, [clientId, fetchCameras])

  const [expandedCamera, setExpandedCamera] = useState<Camera | null>(null)
  const [preferredViewMode, setPreferredViewMode] = useState<
    'grid' | 'carousel'
  >('carousel')
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0)
  const indicatorsRef = useRef<HTMLDivElement>(null)
  const [searchParams] = useSearchParams()
  const appliedCameraParamRef = useRef(false)

  const isMobile = useIsMobile()

  // Pré-seleciona a câmera de quem chega aqui via "Ver ao vivo" no mapa
  // (?camera=...). Só roda uma vez, na primeira carga da lista — depois disso
  // a navegação manual do usuário não pode ser sobrescrita por um refetch.
  useEffect(() => {
    if (appliedCameraParamRef.current || cameras.length === 0) return

    const cameraName = searchParams.get('camera')
    if (cameraName) {
      const index = cameras.findIndex((camera) => camera.name === cameraName)
      if (index >= 0) setCurrentCameraIndex(index)
    }

    appliedCameraParamRef.current = true
  }, [cameras, searchParams])

  // On phones the grid is never an option: it mounts one WebRTC connection per
  // camera, which burns battery and mobile data for a layout that collapses to
  // one camera per row anyway. Derived rather than forced into state so it
  // reacts immediately to a resize and keeps the user's desktop choice.
  const viewMode = isMobile ? 'carousel' : preferredViewMode

  // The index is independent of the list, so a refetch that returns fewer
  // cameras would leave it pointing past the end.
  useEffect(() => {
    setCurrentCameraIndex((prev) =>
      cameras.length > 0 ? Math.min(prev, cameras.length - 1) : 0,
    )
  }, [cameras.length])

  // Keep the active chip in view when navigating with the arrows.
  useEffect(() => {
    const activeChip = indicatorsRef.current?.children[
      currentCameraIndex
    ] as HTMLElement | undefined

    activeChip?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }, [currentCameraIndex])

  const handlePrevCamera = () => {
    setCurrentCameraIndex((prev) =>
      prev === 0 ? cameras.length - 1 : prev - 1,
    )
  }

  const handleNextCamera = () => {
    setCurrentCameraIndex((prev) =>
      prev === cameras.length - 1 ? 0 : prev + 1,
    )
  }

  const handleCameraSelect = (index: number) => {
    setCurrentCameraIndex(index)
  }

  return (
    <div className='stream-view'>
      <div className='stream-header'>
        <div className='header-icon'>
          <i className='pi pi-video' />
        </div>
        <div className='header-content'>
          <h1>Monitoramento em Tempo Real</h1>
          <p>
            {cameras.length} câmera{cameras.length !== 1 ? 's' : ''} ativa
            {cameras.length !== 1 ? 's' : ''}
          </p>
        </div>
        {!isMobile && (
          <div className='view-mode-toggle'>
            <button
              className={`toggle-btn ${viewMode === 'carousel' ? 'active' : ''}`}
              onClick={() => setPreferredViewMode('carousel')}
              title='Visualização Carrossel'
            >
              <i className='pi pi-chevron-circle-right' />
              <span>Carrossel</span>
            </button>
            <button
              className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setPreferredViewMode('grid')}
              title='Visualização em Grade'
            >
              <i className='pi pi-th-large' />
              <span>Grade</span>
            </button>
          </div>
        )}
      </div>

      {viewMode === 'grid' ? (
        <div className='grid'>
          {cameras.map((camera) => (
            <div
              key={camera.id}
              className='col-12 md:col-4'
            >
              <CameraPlayer
                camera={camera}
                onFullscreen={() => setExpandedCamera(camera)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className='carousel-view'>
          <div className='carousel-container'>
            {cameras.length > 0 && (
              <>
                <button
                  className='carousel-nav carousel-nav-prev'
                  onClick={handlePrevCamera}
                  disabled={cameras.length <= 1}
                >
                  <i className='pi pi-chevron-left' />
                </button>

                <div className='carousel-main'>
                  {/* Keyed so switching cameras unmounts the player outright,
                      instead of relying on the effect cleanup to close the
                      previous RTCPeerConnection. */}
                  <CameraPlayer
                    key={cameras[currentCameraIndex].id}
                    camera={cameras[currentCameraIndex]}
                    onFullscreen={() =>
                      setExpandedCamera(cameras[currentCameraIndex])
                    }
                  />
                </div>

                <button
                  className='carousel-nav carousel-nav-next'
                  onClick={handleNextCamera}
                  disabled={cameras.length <= 1}
                >
                  <i className='pi pi-chevron-right' />
                </button>
              </>
            )}
          </div>

          {cameras.length > 1 && (
            <div className='carousel-indicators' ref={indicatorsRef}>
              {cameras.map((camera, index) => (
                <button
                  key={camera.id}
                  className={`carousel-indicator ${index === currentCameraIndex ? 'active' : ''}`}
                  onClick={() => handleCameraSelect(index)}
                  title={camera.name}
                >
                  <span className='indicator-number'>{index + 1}</span>
                  <span className='indicator-name'>{camera.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {expandedCamera && (
        <FullscreenPlayer
          camera={expandedCamera}
          visible={!!expandedCamera}
          onHide={() => setExpandedCamera(null)}
        />
      )}
    </div>
  )
}

export default StreamView
